import {WaiversWorkspace} from './WaiversWorkspace';
import {useEffect,useMemo,useState,type FormEvent} from 'react';
import {CatalogStatusSwitch} from './CatalogStatusSwitch';

type TypeRow={id:string;code:string;name:string;color:string;
  bookingMode?:string;minBookAheadMinutes?:number|null;
  externalRoomSingular?:string;externalRoomPlural?:string;
  customerSingular?:string;customerPlural?:string;};
type ServiceRow={id:string;code:string;name:string;serviceTypeId:string;
  durationMinutes:number;cleanupMinutes:number;priceCents:number|null;
  active:boolean;publicBookingEnabled:boolean;locationActive:boolean;
  locationPublicBookingEnabled:boolean;roomCount:number;rooms?:string[];displayPosition?:number|null;
  description?:string;receiptMessage?:string;allowedWeekdays?:number[];
  earliestStartMinute?:number;latestStartMinute?:number;
  onlineStorePurchasable?:boolean;onlineStoreHighlighted?:boolean;
  sendExpirationEmails?:boolean;
  defaultCreditExpirationDays?:number|null;
  publicBookingMode?:'GENERAL_PUBLIC'|'EXISTING_CREDITS'|'NOT_PUBLIC';
  roomsByLocation?:Record<string,string[]>;};
type Source={types:TypeRow[];rooms?:{id:string;name:string}[];
  locations?:{code:string;name:string;rooms:{id:string;name:string}[]}[];
  services:ServiceRow[]};
const minutesToTime=(m:number)=>String(Math.floor(m/60)).padStart(2,'0')+
  ':'+String(m%60).padStart(2,'0');
const timeToMinutes=(t:string)=>Number(t.slice(0,2))*60+Number(t.slice(3,5));
const weekdays=[{day:1,label:'Mon'},{day:2,label:'Tue'},{day:3,label:'Wed'},
  {day:4,label:'Thu'},{day:5,label:'Fri'},{day:6,label:'Sat'},{day:0,label:'Sun'}];
const money=(cents:number|null)=>cents===null?'Not recorded':
  new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(cents/100);

/** Authenticated FloatHelm-like Services setup from recovered ECFS records.
 * Editing service definition never silently enables public booking. */
export function ServicesWorkspace({locationCode,canEdit,initialMode='services'}:{
  locationCode:string;canEdit:boolean;initialMode?:'services'|'waivers';
}){
  const [catalog,setCatalog]=useState<Source|null>(null);
  const [loadState,setLoadState]=useState('Loading services…');
  const [filter,setFilter]=useState('');
  const [statusFilter,setStatusFilter]=useState<'Enabled'|'Disabled'|'All'>('All');
  const [roomFilter,setRoomFilter]=useState('');
  const [search,setSearch]=useState('');
  const [selected,setSelected]=useState<string|null>(null);
  const [name,setName]=useState('');
  const [serviceTypeId,setServiceTypeId]=useState('');
  const [duration,setDuration]=useState(60);
  const [cleanup,setCleanup]=useState(0);
  const [price,setPrice]=useState('');
  const [saveState,setSaveState]=useState('');
  const [enabled,setEnabled]=useState(false);
  const [locationEnabled,setLocationEnabled]=useState(false);
  const [roomAssignments,setRoomAssignments]=useState<Record<string,string[]>>({});
  const [expandedLocation,setExpandedLocation]=useState<string|null>(null);
  const [description,setDescription]=useState('');
  const [receiptMessage,setReceiptMessage]=useState('');
  const [allowedDays,setAllowedDays]=useState<number[]>([0,1,2,3,4,5,6]);
  const [earliestStart,setEarliestStart]=useState(0);
  const [latestStart,setLatestStart]=useState(1439);
  const [publicBookingMode,setPublicBookingMode]=useState<
    'GENERAL_PUBLIC'|'EXISTING_CREDITS'|'NOT_PUBLIC'>('NOT_PUBLIC');
  const [onlineStorePurchasable,setOnlineStorePurchasable]=useState(false);
  const [onlineStoreHighlighted,setOnlineStoreHighlighted]=useState(false);
  const [sendExpirationEmails,setSendExpirationEmails]=useState(false);
  const [creditNeverExpires,setCreditNeverExpires]=useState(true);
  const [defaultCreditExpirationDays,setDefaultCreditExpirationDays]=useState(0);
  const [editingType,setEditingType]=useState<TypeRow|null>(null);
  const [typeSaveStatus,setTypeSaveStatus]=useState('');
  const [mode,setMode]=useState<'services'|'service-types'|'waivers'|'reorder'>(initialMode);
  const [draftOrder,setDraftOrder]=useState<string[]>([]);
  const [busy,setBusy]=useState(false);
  const [statusBusy,setStatusBusy]=useState<string|null>(null);
  const reload=async(signal?:AbortSignal)=>{
    const response=await fetch('/api/v1/staff/services?'+new URLSearchParams({
      location:locationCode,
    }),{credentials:'same-origin',cache:'no-store',signal});
    if(!response.ok)throw Error('Services unavailable.');
    const body=await response.json() as {ok:boolean;data?:Source};
    if(!body.ok||!body.data||!Array.isArray(body.data.services)||
      !Array.isArray(body.data.types))throw Error('Invalid Services catalog.');
    return body.data;
  };
  useEffect(()=>{
    const controller=new AbortController();setCatalog(null);setSelected(null);
    setLoadState('Loading services…');
    void reload(controller.signal).then(data=>{if(!controller.signal.aborted){
      setCatalog(data);setDraftOrder(data.services.map(x=>x.id));setLoadState('');
    }}).catch(()=>{if(!controller.signal.aborted)setLoadState('Services unavailable.');});
    return ()=>controller.abort();
  },[locationCode]);
  const record=catalog?.services.find(item=>item.id===selected)??null;
  const types=useMemo(()=>catalog?.types??[],[catalog]);
  const shown=useMemo(()=>catalog?.services.filter(service=>
    (!filter||service.serviceTypeId===filter)&&
    (statusFilter==='All'||(statusFilter==='Enabled'?service.active:!service.active))&&
    (!roomFilter||service.rooms?.includes(roomFilter))&&
    (!search.trim()||[service.name,service.code].some(value=>
      value.toLowerCase().includes(search.trim().toLowerCase()))) )??[],
    [catalog,filter,statusFilter,roomFilter,search]);
  const choose=(item:ServiceRow)=>{
    setSelected(item.id);setName(item.name);setServiceTypeId(item.serviceTypeId);
    setDuration(item.durationMinutes);
    setCleanup(item.cleanupMinutes);setEnabled(item.active);
    setLocationEnabled(item.locationActive);
    setRoomAssignments(item.roomsByLocation??{[locationCode]:item.rooms??[]});
    setExpandedLocation(null);
    setDescription(item.description??'');
    setReceiptMessage(item.receiptMessage??'');
    setAllowedDays(item.allowedWeekdays??[0,1,2,3,4,5,6]);
    setEarliestStart(item.earliestStartMinute??0);
    setLatestStart(item.latestStartMinute??1439);
    setPublicBookingMode(item.publicBookingMode??(item.publicBookingEnabled?
      'GENERAL_PUBLIC':'NOT_PUBLIC'));
    setOnlineStorePurchasable(item.onlineStorePurchasable??false);
    setOnlineStoreHighlighted(item.onlineStoreHighlighted??false);
    setSendExpirationEmails(item.sendExpirationEmails??false);
    setCreditNeverExpires(item.defaultCreditExpirationDays===null||
      item.defaultCreditExpirationDays===undefined);
    setDefaultCreditExpirationDays(item.defaultCreditExpirationDays??0);
    setPrice(item.priceCents===null?'':(item.priceCents/100).toFixed(2));
    setSaveState('');
  };
  const save=async(event:FormEvent)=>{
    event.preventDefault();if(!record||busy||!canEdit)return;
    const asPrice=price.trim()===''?null:Math.round(Number(price)*100);
    if(price.trim()!==''&&(!Number.isFinite(asPrice)||asPrice!<0)){
      setSaveState('Enter a valid nonnegative price.');return;
    }
    setBusy(true);setSaveState('Saving…');
    try{
      const response=await fetch('/api/v1/staff/services/'+encodeURIComponent(record.id),{
        method:'PATCH',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,name:name.trim(),
          serviceTypeId,description,receiptMessage,
          durationMinutes:duration,cleanupMinutes:cleanup,priceCents:asPrice,
          allowedWeekdays:allowedDays,earliestStartMinute:earliestStart,
          latestStartMinute:latestStart,onlineStorePurchasable,
          onlineStoreHighlighted,sendExpirationEmails,publicBookingMode,
          defaultCreditExpirationDays:creditNeverExpires?null:defaultCreditExpirationDays,
          active:enabled,locationActive:locationEnabled,
          publicBookingEnabled:publicBookingMode!=='NOT_PUBLIC',
          locationPublicBookingEnabled:publicBookingMode!=='NOT_PUBLIC',
          roomAssignments}),
      });
      if(!response.ok){
        const details=await response.json().catch(()=>null) as
          {error?:{code?:string;message?:string}}|null;
        throw Error(response.status===403?'Service edit access denied.':
          'Service could not be saved: '+(details?.error?.message??details?.error?.code??response.status));
      }
      const next=await reload();setCatalog(next);setSaveState('Saved');
    }catch(error){setSaveState(error instanceof Error?error.message:'Save failed.');}
    finally{setBusy(false);}
  };
  const setServiceEnabled=async(item:ServiceRow)=>{
    if(!canEdit||busy||statusBusy)return;
    setStatusBusy(item.id);setSaveState('');
    try{
      const response=await fetch('/api/v1/staff/services/'+
        encodeURIComponent(item.id)+'/status',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,enabled:!item.active}),
      });
      const body=await response.json() as {ok:boolean;error?:{code?:string}};
      if(!response.ok||!body.ok)throw Error(body.error?.code??'Status not saved.');
      const next=await reload();setCatalog(next);setSelected(null);
      setStatusFilter('All');
      setSaveState(item.name+(item.active?' disabled.':' enabled.'));
    }catch(e){setSaveState(e instanceof Error?e.message:'Status not saved.');}
    finally{setStatusBusy(null);}
  };
  const saveType=async(event:FormEvent)=>{
    event.preventDefault();
    if(!editingType||!canEdit||busy)return;
    setBusy(true);setTypeSaveStatus('Saving…');
    try{
      const response=await fetch('/api/v1/staff/service-types/'+editingType.id,{
        method:'PATCH',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,
          name:editingType.name,color:editingType.color,
          minBookAheadMinutes:editingType.minBookAheadMinutes??null,
          externalRoomSingular:editingType.externalRoomSingular??'',
          externalRoomPlural:editingType.externalRoomPlural??'',
          customerSingular:editingType.customerSingular??'',
          customerPlural:editingType.customerPlural??'',
        }),
      });
      if(!response.ok){
        const result=await response.json().catch(()=>null) as
          {error?:{code?:string}}|null;
        throw Error(result?.error?.code??'Unable to save Service Type.');
      }
      const next=await reload();setCatalog(next);
      const fresh=next.types.find(t=>t.id===editingType.id);
      if(fresh)setEditingType(fresh);
      setTypeSaveStatus('Service Type saved.');
    }catch(e){setTypeSaveStatus(e instanceof Error?e.message:'Unable to save Service Type.');}
    finally{setBusy(false);}
  };
  const move=(index:number,amount:number)=>{
    setDraftOrder(prev=>{const result=[...prev],newIndex=index+amount;
      if(newIndex<0||newIndex>=result.length)return prev;
      [result[index],result[newIndex]]=[result[newIndex],result[index]];return result;});
  };
  const saveOrder=async()=>{
    if(!canEdit||busy)return;setBusy(true);setSaveState('Saving order…');
    try{
      const response=await fetch('/api/v1/staff/services/order',{
        method:'POST',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,serviceIds:draftOrder}),
      });
      if(!response.ok){
        const details=await response.json().catch(()=>null) as
          {error?:{code?:string;message?:string}}|null;
        throw Error('Unable to save service order: '+
          (details?.error?.message??details?.error?.code??response.status));
      }
      const next=await reload();setCatalog(next);setDraftOrder(next.services.map(x=>x.id));
      setSaveState('Service order saved for staff and public booking.');
    }catch(e){setSaveState(e instanceof Error?e.message:'Unable to save order.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-services-workspace" aria-label="Services">
    <header className="ecfs-products-heading"><h1>Services</h1>
      <span>Bookable and sellable Services</span></header>
    {loadState&&<p role="status">{loadState}</p>}
    {catalog&&<>
      <nav className="ecfs-services-setup-links" aria-label="Services setup links">
        <button type="button" onClick={()=>setMode('service-types')}>Setup Service Types</button>
        <button type="button" onClick={()=>setMode('waivers')}>Setup Waivers</button>
        <button type="button" onClick={()=>{setMode('reorder');setSaveState('');}}>Reorder</button>
        {mode!=='services'&&<button type="button" onClick={()=>setMode('services')}>Services</button>}
      </nav>
      {mode==='reorder'&&<section className="ecfs-service-reorder" aria-label="Reorder Services">
        <h2>Reorder Services</h2><p>Drag or use arrows to choose the Services order for staff and public booking.</p>
        {draftOrder.map((id,index)=>{const service=catalog.services.find(x=>x.id===id);
          return <div key={id} className="ecfs-service-order-row" draggable={canEdit&&!busy}
            onDragStart={e=>e.dataTransfer.setData('text/plain',id)}
            onDragOver={e=>e.preventDefault()}
            onDrop={e=>{e.preventDefault();if(!canEdit||busy)return;
              const source=e.dataTransfer.getData('text/plain');
              setDraftOrder(prev=>{const from=prev.indexOf(source),to=prev.indexOf(id);
                if(from<0||to<0)return prev;
                const next=[...prev];next.splice(from,1);next.splice(to,0,source);
                return next;});}} aria-label={'Reorder '+(service?.name??id)}>
            <span>{service?.name??id}</span>
            <button type="button" disabled={index===0||!canEdit||busy}
              aria-label={'Move '+service?.name+' up'} onClick={()=>move(index,-1)}>↑</button>
            <button type="button" disabled={index===draftOrder.length-1||!canEdit||busy}
              aria-label={'Move '+service?.name+' down'} onClick={()=>move(index,1)}>↓</button>
          </div>;})}
        <button type="button" disabled={!canEdit||busy} onClick={()=>void saveOrder()}>Save Order</button>
        {saveState&&<p role="status">{saveState}</p>}
      </section>}
      {mode==='service-types'&&<section aria-label="Service types setup">
        <h2>Service Types</h2>
        <div className="ecfs-service-types-rows">
          {catalog.types.map(type=><div key={type.id} className="ecfs-service-type-row">
            <button type="button" aria-expanded={editingType?.id===type.id}
              onClick={()=>{setEditingType(type);setTypeSaveStatus('');}}>
              <span aria-hidden="true" style={{backgroundColor:type.color,
                display:'inline-block',height:14,width:14,borderRadius:3,
                marginRight:7}}/>
              {type.name}
            </button>
            {editingType?.id===type.id&&<form className="ecfs-service-type-editor"
              aria-label={'Edit '+type.name+' Service Type'} onSubmit={e=>void saveType(e)}>
              <label>Service Type name
                <input value={editingType.name} minLength={1} maxLength={140} required
                  disabled={!canEdit||busy}
                  onChange={e=>setEditingType(v=>v?{...v,name:e.target.value}:v)}/>
              </label>
              <label>Service Type color
                <input type="color" value={editingType.color}
                  disabled={!canEdit||busy}
                  onChange={e=>setEditingType(v=>v?{...v,color:e.target.value}:v)}/>
              </label>
              <p><strong>Booking Mode:</strong> {editingType.bookingMode??'Basic'}</p>
              <fieldset><legend>Online booking</legend>
                <label>Online booking of services must be done at least
                  <input type="number" aria-label="Book-ahead hours" min={0} step="0.25"
                    value={editingType.minBookAheadMinutes===null||
                      editingType.minBookAheadMinutes===undefined?''
                      :editingType.minBookAheadMinutes/60}
                    disabled={!canEdit||busy}
                    onChange={e=>setEditingType(v=>v?{...v,
                      minBookAheadMinutes:e.target.value===''?null:
                        Math.round(Number(e.target.value)*60)}:v)}/>
                  hours ahead of appointment (optional)
                </label>
              </fieldset>
              <fieldset><legend>External Name for Room</legend>
                <label>Singular
                  <input aria-label="External room singular" maxLength={100}
                    value={editingType.externalRoomSingular??''} disabled={!canEdit||busy}
                    onChange={e=>setEditingType(v=>v?{...v,
                      externalRoomSingular:e.target.value}:v)}/>
                </label>
                <label>Plural
                  <input aria-label="External room plural" maxLength={100}
                    value={editingType.externalRoomPlural??''} disabled={!canEdit||busy}
                    onChange={e=>setEditingType(v=>v?{...v,
                      externalRoomPlural:e.target.value}:v)}/>
                </label>
              </fieldset>
              <fieldset><legend>Internal Name for Customer</legend>
                <label>Singular
                  <input aria-label="Internal customer singular" maxLength={100}
                    value={editingType.customerSingular??''} disabled={!canEdit||busy}
                    onChange={e=>setEditingType(v=>v?{...v,
                      customerSingular:e.target.value}:v)}/>
                </label>
                <label>Plural
                  <input aria-label="Internal customer plural" maxLength={100}
                    value={editingType.customerPlural??''} disabled={!canEdit||busy}
                    onChange={e=>setEditingType(v=>v?{...v,
                      customerPlural:e.target.value}:v)}/>
                </label>
              </fieldset>
              {canEdit&&<button type="submit" disabled={busy}>Save Service Type</button>}
              <button type="button" onClick={()=>setEditingType(null)}>cancel</button>
              {typeSaveStatus&&<p role="status">{typeSaveStatus}</p>}
            </form>}
          </div>)}
        </div>
      </section>}
      {mode==='waivers'&&<WaiversWorkspace locationCode={locationCode} canEdit={canEdit}/>}
      {mode==='services'&&<div className="ecfs-services-filters" aria-label="Service filters">
        <div role="group" aria-label="Service status">
          {(['Enabled','Disabled','All'] as const).map(option=>
            <button type="button" key={option} aria-pressed={statusFilter===option}
              onClick={()=>setStatusFilter(option)}>{option}</button>)}
        </div>
        <select aria-label="Service Type" value={filter}
          onChange={event=>setFilter(event.target.value)}>
          <option value="">Service Type</option>
          {types.map(type=><option value={type.id} key={type.id}>{type.name}</option>)}
        </select>
        <select aria-label="Any Room" value={roomFilter}
          onChange={event=>setRoomFilter(event.target.value)}>
          <option value="">Any Room</option>
          {(catalog.rooms??[]).map(room=><option key={room.id} value={room.id}>
            {room.name}</option>)}
        </select>
        <input type="search" aria-label="Filter Services" placeholder="Filter Services"
          value={search} onChange={event=>setSearch(event.target.value)}/>
      </div>}
      {mode==='services'&&<div className="ecfs-service-setup">
        <div className="ecfs-services-table-scroll">
          <div className="ecfs-services-list" role="list" aria-label="Services list">
            <div className="ecfs-services-list-head" aria-hidden="true">
              {['Status','Type','Service','Service Length','Block Length',
                'Price','Days','Rooms','Time','Online Store'].map(label=>
                <span key={label}>{label}</span>)}
            </div>
            {shown.map(item=><div role="listitem" aria-label={item.name}
              className="ecfs-service-list-row" key={item.id}
              aria-current={item.id===selected?'true':undefined}
              tabIndex={0} onKeyDown={event=>{
                if(event.key==='Enter'||event.key===' '){
                  event.preventDefault();choose(item);
                }
              }} onClick={()=>choose(item)}>
              <CatalogStatusSwitch enabled={item.active}
                label={item.name+' enabled'} busy={statusBusy===item.id}
                disabled={!canEdit||busy} onToggle={()=>void setServiceEnabled(item)}/>
              <span>{types.find(type=>type.id===item.serviceTypeId)?.name??'Service'}</span>
              <strong>{item.name}</strong>
              <span>{item.durationMinutes} mins</span>
              <span>{item.durationMinutes+item.cleanupMinutes} mins</span>
              <span>{money(item.priceCents)}</span>
              <span>{weekdays.filter(d=>(item.allowedWeekdays??[0,1,2,3,4,5,6])
                .includes(d.day)).map(d=>d.label).join(' ')}</span>
              <span>{item.roomCount}</span>
              <span>{minutesToTime(item.earliestStartMinute??0)} – {
                minutesToTime(item.latestStartMinute??1439)}</span>
              <span>{item.onlineStorePurchasable?'Purchasable':'—'}</span>
            </div>)}
            {shown.length===0&&<p>No services in this category.</p>}
          </div>
        </div>
        {record&&<form className="ecfs-service-detail" onSubmit={event=>void save(event)}>
          <h2>{record.name}</h2>
          <p><strong>Service code:</strong> {record.code}</p>
          <label>Service Type
            <select aria-label="Service Type for selected service" value={serviceTypeId}
              onChange={e=>setServiceTypeId(e.target.value)} disabled={!canEdit||busy}>
              {types.map(type=><option value={type.id} key={type.id}>{type.name}</option>)}
            </select>
          </label>
          <label>Service name
            <input aria-label="Service name" value={name} minLength={2} maxLength={140}
              onChange={event=>setName(event.target.value)} disabled={!canEdit||busy} required/>
          </label>
          <label>Description
            <textarea aria-label="Service description" rows={3} maxLength={5000}
              value={description} disabled={!canEdit||busy}
              onChange={e=>setDescription(e.target.value)}/>
          </label>
          <label>Sales Receipt Message for Customers
            <textarea aria-label="Sales receipt message" rows={2} maxLength={5000}
              value={receiptMessage} disabled={!canEdit||busy}
              onChange={e=>setReceiptMessage(e.target.value)}/>
          </label>
          <label>Minutes of Service
            <input aria-label="Minutes of Service" type="number" min={5} max={1440}
              value={duration} onChange={e=>setDuration(Number(e.target.value))}
              disabled={!canEdit||busy} required/>
          </label>
          <label>Minutes Used on Schedule
            <input aria-label="Minutes Used on Schedule" type="number"
              min={duration} max={2160} step={1} value={duration+cleanup}
              onChange={e=>setCleanup(Math.max(0,Number(e.target.value)-duration))}
              disabled={!canEdit||busy} required/>
          </label>
          <fieldset><legend>Pricing</legend>
            <label>Price ($)
              <input aria-label="Service price" type="number" min={0} step="0.01"
                value={price} onChange={e=>setPrice(e.target.value)}
                disabled={!canEdit||busy} placeholder="Not recorded"/>
            </label>
          </fieldset>
          <fieldset className="ecfs-service-time-settings"><legend>Limit Availability</legend>
            <p>Available on these days of the week</p>
            <div role="group" aria-label="Service weekdays" className="ecfs-service-days">
              {weekdays.map(({day,label})=><label key={day}>
                <input type="checkbox" aria-label={label} checked={allowedDays.includes(day)}
                  disabled={!canEdit||busy}
                  onChange={e=>setAllowedDays(current=>e.target.checked
                    ?[...current,day]:current.filter(x=>x!==day))}/>
                {label}</label>)}
            </div>
            <div className="ecfs-service-hours">
              <label>From Hour
                <input type="time" aria-label="Service earliest start"
                  value={minutesToTime(earliestStart)}
                  disabled={!canEdit||busy}
                  onChange={e=>setEarliestStart(timeToMinutes(e.target.value))}/>
              </label>
              <label>To Hour
                <input type="time" aria-label="Service latest start"
                  value={minutesToTime(latestStart)}
                  disabled={!canEdit||busy}
                  onChange={e=>setLatestStart(timeToMinutes(e.target.value))}/>
              </label>
            </div>
            <p>Service start times within the location's normal booking window.</p>
          </fieldset>
          <fieldset className="ecfs-service-rooms">
            <legend>Rooms</legend>
            {(catalog.locations??[{code:locationCode,
              name:locationCode,rooms:catalog.rooms??[]}]).map(loc=>{
              const chosen=roomAssignments[loc.code]??[];
              const count=chosen.length;
              const open=expandedLocation===loc.code;
              return <section key={loc.code} className="ecfs-service-room-location">
                <button type="button" className="ecfs-service-room-toggle"
                  aria-expanded={open} aria-controls={'service-rooms-'+loc.code}
                  onClick={()=>setExpandedLocation(open?null:loc.code)}>
                  {count===0?'no rooms selected':'available in '+count+
                    (count===1?' room':' rooms')} at {loc.name}
                </button>
                {open&&<div id={'service-rooms-'+loc.code}
                  className="ecfs-service-room-location-options">
                  <h3>{loc.name}</h3>
                  {loc.rooms.map(room=><label key={room.id}>
                    <input type="checkbox" checked={chosen.includes(room.id)}
                      disabled={!canEdit||busy}
                      onChange={e=>setRoomAssignments(current=>{
                        const existing=current[loc.code]??[];
                        return {...current,[loc.code]:e.target.checked
                          ?[...existing,room.id]
                          :existing.filter(id=>id!==room.id)};
                      })}/>
                    {room.name}</label>)}
                  {!loc.rooms.length&&<p>No configured rooms at this location.</p>}
                </div>}
              </section>;
            })}
            <p className="ecfs-service-room-note">
              NOTE: If a service doesn't apply to any rooms at a location, it will not show up
              in that location's Online Store. Packages and Memberships containing this
              service will also be hidden there.
            </p>
          </fieldset>
          <fieldset className="ecfs-service-public-mode"><legend>Online Booking</legend>
            <label><input type="radio" name="public-booking-mode"
                checked={publicBookingMode==='GENERAL_PUBLIC'}
                onChange={()=>setPublicBookingMode('GENERAL_PUBLIC')}
                disabled={!canEdit||busy}/>
              Bookable Online for General Public</label>
            <label><input type="radio" name="public-booking-mode"
                checked={publicBookingMode==='EXISTING_CREDITS'}
                onChange={()=>setPublicBookingMode('EXISTING_CREDITS')}
                disabled={!canEdit||busy}/>
              Bookable Online only by Customers who have existing Service Credits</label>
            <label><input type="radio" name="public-booking-mode"
                checked={publicBookingMode==='NOT_PUBLIC'}
                onChange={()=>setPublicBookingMode('NOT_PUBLIC')}
                disabled={!canEdit||busy}/>
              Not Bookable Online for General Public</label>
          </fieldset>
          <fieldset className="ecfs-service-store"><legend>Online Store</legend>
            <label><input type="checkbox" checked={onlineStorePurchasable}
              disabled={!canEdit||busy}
              onChange={e=>setOnlineStorePurchasable(e.target.checked)}/>
              Purchasable by General Public in your Online Store</label>
            <label><input type="checkbox" checked={onlineStoreHighlighted}
              disabled={!canEdit||busy}
              onChange={e=>setOnlineStoreHighlighted(e.target.checked)}/>
              Highlighted in your Online Store</label>
          </fieldset>
          <fieldset className="ecfs-service-expiration"><legend>Default Service Credit Expiration</legend>
            <label><input type="checkbox" aria-label="Service credits never expire"
              disabled={!canEdit||busy} checked={creditNeverExpires}
              onChange={event=>setCreditNeverExpires(event.target.checked)}/>
              No expiration</label>
            {!creditNeverExpires&&<label>Expire after (days)
              <input type="number" min={0} max={36500} step={1}
                aria-label="Default service credit expiration days"
                disabled={!canEdit||busy} value={defaultCreditExpirationDays}
                onChange={event=>setDefaultCreditExpirationDays(Number(event.target.value))}
                required/>
            </label>}
            <p>Changes do not apply to already-sold credits.</p>
          </fieldset>
          <fieldset className="ecfs-service-switches"><legend>Service Status</legend>
            <label><input type="checkbox" checked={sendExpirationEmails}
              disabled={!canEdit||busy}
              onChange={e=>setSendExpirationEmails(e.target.checked)}/>
              Send Expiration Emails</label>
          </fieldset>
          {allowedDays.length===0&&<p role="alert">Select at least one day.</p>}
          {earliestStart>latestStart&&
            <p role="alert">From Hour cannot be later than To Hour.</p>}
          {canEdit&&<button type="submit" disabled={busy||!allowedDays.length||earliestStart>latestStart}>{busy?'Saving…':'Save'}</button>}
          {saveState&&<p role="status">{saveState}</p>}
        </form>}
      </div>}
    </>}
  </section>;
}
